import type { ExtractionStage } from '../../../api/listeners/document-extraction'

export type ListenerNames = { lastName: string; firstName: string; secondName: string }
export type RecognizedNames = Partial<ListenerNames>

const separators = "- '\u2019\u02bc\u2010\u2011"
const namePattern = new RegExp(`^\\p{L}[\\p{L}\\p{M}]*(?:[${separators}][\\p{L}\\p{M}]+)*$`, 'u')
const segmentStart = new RegExp(`(^|[${separators}])(\\p{L})`, 'gu')

/** Match the case used by listener records without changing the spelling. */
export const normalizeExtractedName = (value: string): string | null => {
    const text = value.normalize('NFC').trim().replace(/\s+/gu, ' ')
    if (!namePattern.test(text)) return null
    const normalized = text.toLowerCase().replace(segmentStart, (_, separator: string, letter: string) => separator + letter.toUpperCase()).normalize('NFC')
    return Array.from(normalized).length <= 80 ? normalized : null
}

const fields = [
    ['lastName', 'lastName'], ['firstName', 'firstName'], ['middleName', 'secondName'],
] as const
const unconfirmedLinks = new Set(['person_link_unconfirmed', 'document_link_unconfirmed'])

export const recognizedNamePackets = (stage: ExtractionStage | null): { packets: RecognizedNames[]; needsReview: boolean } => {
    let packets: RecognizedNames[] = []
    let needsReview = false
    if (!stage || !['succeeded', 'failed'].includes(stage.status)) return { packets, needsReview }
    for (const part of stage.parts) {
        if (part.status !== 'succeeded' || !part.result) continue
        for (const document of part.result.documents) {
            // A diploma or name-change certificate may contain a previous name.
            if (document.type !== 'passport_main') continue
            const packet: RecognizedNames = {}
            for (const [source, target] of fields) {
                const field = document.fields.find(item => item.name === source)
                if (!field || ['not_present', 'not_applicable'].includes(field.status)) continue
                const rawText = field.rawValue?.trim() || ''
                const normalizedText = field.normalizedValue?.trim() || ''
                if (!rawText && !normalizedText && field.status === 'extracted') continue
                const raw = rawText ? normalizeExtractedName(rawText) : null
                const normalized = normalizedText ? normalizeExtractedName(normalizedText) : null
                const value = normalized || raw
                const conflicting = part.result.conflicts.some(conflict => !unconfirmedLinks.has(conflict.code)
                    && conflict.fields.some(ref => ref.documentId === document.documentId && ref.field === source))
                    || part.result.unresolved.some(item => !unconfirmedLinks.has(item.code) && item.documentId === document.documentId && item.field === source)
                if (!value || field.status !== 'extracted' || field.candidates.length
                    || ['failed', 'invalid', 'conflict'].includes(field.validation.format)
                    || ['failed', 'invalid', 'conflict'].includes(field.validation.crossDocument)
                    || (rawText && !raw) || (normalizedText && !normalized) || (raw && normalized && raw !== normalized)) {
                    needsReview = true
                    continue
                }
                if (conflicting) needsReview = true
                packet[target] = value
            }
            if (Object.keys(packet).length && !packets.some(item => JSON.stringify(item) === JSON.stringify(packet))) packets.push(packet)
        }
    }
    // Prefer a complete existing passport over its repeated partial pages. Do
    // not synthesize a name by combining fields from separate documents.
    const complete = packets.find(candidate => packets.every(packet => fields.every(([, key]) => packet[key] === undefined || candidate[key] === packet[key])))
    if (complete) packets = [complete]
    return { packets, needsReview }
}
